import { useEffect } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { persistStore } from 'redux-persist';
import { useDispatch, useSelector } from 'react-redux';
import { saveUser, updateUserToken, setLoading } from 'app/auth';
import { getIdToken, signOut, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { api } from 'store/api';
import { store } from 'app/store';
import { reset } from 'app/playlist';
import { api as service } from 'utils/axios';
import { auth } from 'utils/firebase';
import { registerUserInNewBackend } from 'utils/newBackendApi';

import MyChest from 'routes/my-chest';
import Root from 'routes/root';
import SignIn from 'routes/sign-in-default';
import SignInBeta from 'routes/sign-in-beta';
import ProtectedRoute from 'utils/ProtectedRoute';
import DisconnectedRoute from 'utils/DisconnectedRoute';
import SignUp from 'routes/sign-up';
import Manage from 'routes/manage';
import Notifications from 'routes/notifications';
import RecentlyPlayed from 'routes/recently-played';
import Permissions from 'routes/permissions';
import Account from 'routes/account';
import Security from 'routes/security';
import Terms from 'routes/terms';
import Help from 'routes/help';
import Shared from 'routes/shared';
import Profile from 'routes/profile';
import Setup from 'routes/setup';
import Upload from 'routes/upload';
import Share from 'routes/share';
import Treasure from 'routes/treasure';
import Trash from 'routes/trash';
import PasswordReset from 'routes/password-reset';
import Subscription from 'routes/subscription';
import Plan from 'routes/plan';
import Referral from 'routes/referral';
import NewPassword from 'routes/new-password';
import Loading from 'components/Loading';

function App() {
  const user = useSelector((state) => state.auth.user);
  const isLoading = useSelector((state) => state.auth.isLoading);
  const dispatch = useDispatch();
  const router = createBrowserRouter([
    {
      path: '/',
      element: <Root />,
      errorElement: <Navigate replace to='/sign-in' />,
      children: [
        {
          index: true,
          element: <Navigate replace to='/sign-in' />,
        },
        {
          path: '/sign-in',
          element: <DisconnectedRoute>{process.env.REACT_APP_BETA ? <SignInBeta /> : <SignIn />}</DisconnectedRoute>,
        },
        {
          path: '/sign-up',
          element: <SignUp />,
        },
        {
          path: '/password-reset',
          element: <PasswordReset />,
        },
        {
          path: '/new-password',
          element: <NewPassword />,
        },
        {
          path: '/my-chest',
          element: (
            <ProtectedRoute onlyArtist={true}>
              <MyChest />
            </ProtectedRoute>
          ),
        },
        {
          path: '/my-chest/upload',
          element: (
            <ProtectedRoute onlyArtist={true}>
              <Upload />
            </ProtectedRoute>
          ),
        },
        {
          path: '/my-chest/treasure/:id',
          element: (
            <ProtectedRoute onlyArtist={true}>
              <Treasure />
            </ProtectedRoute>
          ),
        },
        {
          path: '/my-chest/treasure/:id/trash',
          element: (
            <ProtectedRoute onlyArtist={true}>
              <Trash />
            </ProtectedRoute>
          ),
        },
        {
          path: 'shared',
          element: (
            <ProtectedRoute>
              <Shared />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile',
          element: (
            <ProtectedRoute onlyAmbassador={false}>
              <Profile />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile/account',
          element: (
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile/help',
          element: (
            <ProtectedRoute>
              <Help />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile/account/subscription',
          element: (
            <ProtectedRoute>
              <Subscription />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile/account/subscription/plan',
          element: (
            <ProtectedRoute>
              <Plan />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile/terms',
          element: (
            <ProtectedRoute>
              <Terms />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile/referral',
          element: (
            <ProtectedRoute onlyAmbassador={true}>
              <Referral />
            </ProtectedRoute>
          ),
        },
        {
          path: 'profile/security',
          element: (
            <ProtectedRoute>
              <Security />
            </ProtectedRoute>
          ),
        },
        {
          path: 'notifications',
          element: (
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          ),
        },
        {
          path: 'setup',
          element: (
            <ProtectedRoute>
              <Setup />
            </ProtectedRoute>
          ),
        },
        {
          path: 'share/:trackId/:versionId',
          element: <Share />,
        },
      ],
    },
  ]);

  useEffect(() => {
    // Handle email verification link when user returns from email
    const handleEmailVerificationLink = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        try {
          // Get email from localStorage (stored when sending verification)
          let email = window.localStorage.getItem('emailForSignIn');
          if (!email) {
            // If not found, prompt user (this shouldn't happen in normal flow)
            email = window.prompt('Please provide your email for verification');
          }

          if (email) {
            const result = await signInWithEmailLink(auth, email, window.location.href);
            // Clear the email from storage
            window.localStorage.removeItem('emailForSignIn');
            console.log('Email verification successful:', result.user.email);
          }
        } catch (error) {
          console.error('Error during email verification:', error);
        }
      }
    };

    handleEmailVerificationLink();

    const getToken = async (user) => {
      try {
        // force token refresh
        const token = await getIdToken(user, true);
        const response = await service.get('account/');
        const provider = user?.providerData[0].providerId;
        
        //  NEW: Register user in new backend (non-blocking)
        registerUserInNewBackend(user).catch(error => {
          console.warn('New backend registration failed, but continuing with normal flow:', error);
        });
        
        dispatch(
          saveUser({
            data: response.data,
            token: token,
            email: user?.email,
            uid: user?.uid, // Add Firebase UID for onboarding
            signInMethod: provider === 'google.com' ? 'google' : 'local',
          })
        );

        dispatch(setLoading(false));
      } catch (error) {
        console.error('Error getting token:', error);
        dispatch(setLoading(false));
        await signOut(auth);
      }
    };

    auth.onAuthStateChanged((user) => {
      if (user) {
        getToken(user);
      } else {
        dispatch(reset());
        dispatch(saveUser(undefined));
        dispatch(setLoading(false));
        api.util.resetApiState();
        persistStore(store).purge();
      }
    });

    // if no user after 2 seconds, set loading to false
    const timeout = setTimeout(() => {
      if (!auth.currentUser) {
        dispatch(setLoading(false));
      }
    }, 2000);


    // Token refresh listener - only update Redux token, don't make API calls
    auth.onIdTokenChanged(async (firebaseUser) => {
      if (user && user.token && firebaseUser && auth.currentUser) {
        try {
          const newToken = await firebaseUser.getIdToken(false); // Don't force refresh here
          if (newToken !== user.token) {
            dispatch(updateUserToken(newToken));
          }
        } catch (error) {
          console.error('Error in token change listener:', error);
        }
      }
    });
     return () => clearTimeout(timeout);
  }, []);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Loading />
      </div>
    );
  }

  return <RouterProvider router={router} />;
}

export default App;
